/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import CurrencyInputField from 'react-currency-input-field'
import Text from '../Text'
import { paragraphMedium, weightRegular } from 'src/components/css'
import { ErrorLabel } from '../ErrorLabel'
import { SerializedStyles } from '@emotion/react'
import { useState, useEffect, useMemo, useCallback } from 'react'
import {
  getLocaleConfig,
  type CountryLocaleInput,
} from '@sportninja/common/currency'

/**
 * SN-4709 (James, Aug 12) — "that should be grayed out though", said about a
 * field that was ALREADY non-editable (the Deposit under Team-Managed Budget).
 *
 * Dimming the TEXT was the only disabled treatment this component had, so a
 * disabled money field kept its raised fill, its full-contrast border and a text
 * cursor — it looked exactly like the editable one beside it. `react-currency-
 * input-field` renders a bare input, so unlike the antd-backed TextInput it
 * inherits no disabled styling from anywhere else; this is the only place it can
 * come from.
 *
 * Applied in the COMPONENT rather than at that one call site, so every disabled
 * money field in the app reads as disabled. Recessed fill + muted border +
 * not-allowed: the field keeps its shape (an admin still needs to see WHAT
 * stopped applying) but stops looking like somewhere to type.
 */
const DISABLED_FIELD_STYLE = `
  color: ${colors.NEUTRAL_100};
  background: ${colors.SECONDARY_300};
  border-color: ${colors.SECONDARY_200};
  cursor: not-allowed;
`

type CurrencyInputProps = {
  label: string
  /**
   * Pass null/undefined to leave the field genuinely empty so the placeholder
   * shows. A numeric 0 still renders as a real "0.00" value, which is what every
   * existing caller relies on.
   */
  value: number | null | undefined
  disabled?: boolean
  onChange: (value: number) => void
  /**
   * Optional: the amount as typed, on every keystroke (null while empty) — for a caller whose
   * logic must see what is on screen before the field loses focus (e.g. Enter submitting the
   * form). Never feed it back into `value`: that would reformat the field mid-typing. `onChange`
   * still commits on blur, as before.
   */
  onValueChange?: (value: number | null) => void
  min?: number
  fieldErrors?: string[]
  /**
   * A message for this field, keyed by `name` (`{ [name]: message }`) — what
   * ErrorLabel reads. A bare string is accepted for older callers.
   */
  customError?: string | Record<string, string>
  name: string
  selectStyle?: SerializedStyles
  country?: CountryLocaleInput
}

/**
 * CurrencyInput component that uses react-currency-input-field
 * Always displays in CAD/USD format with 2 decimal places
 * Only triggers onChange on blur to preserve decimal formatting
 *
 * @param {CurrencyInputProps} props - The component props
 * @returns {JSX.Element} - The rendered component
 */
const CurrencyInput = ({
  label,
  value,
  disabled,
  onChange,
  onValueChange,
  min = 0,
  fieldErrors = [],
  customError = '',
  name,
  selectStyle,
  country,
}: CurrencyInputProps) => {
  const localeConfig = useMemo(() => getLocaleConfig(country), [country])

  // Convert the value to string for the input field.
  // A null/undefined value maps to '' so the field stays empty and the
  // placeholder shows; a numeric 0 still maps to '0' (note `?? ''` rather than
  // `|| '0'`, which would have turned 0 into a rendered value too).
  const stringValue = value === null || value === undefined ? '' : String(value)

  // Internal state to track input value during typing
  const [internalValue, setInternalValue] = useState(stringValue)

  /**
   * Ensures the value has decimal places to avoid formatting issues
   * Uses the locale-appropriate decimal separator
   */
  const ensureDecimalFormat = useCallback(
    (val: string): string => {
      // An empty field must stay empty — otherwise it would render as ".00".
      if (val === '') {
        return ''
      }
      if (!val.includes(localeConfig.decimalSeparator)) {
        return `${val}${localeConfig.decimalSeparator}${'0'.repeat(
          localeConfig.decimals
        )}`
      }
      return val
    },
    [localeConfig.decimalSeparator, localeConfig.decimals]
  )

  // Update internal value when prop value changes or locale changes
  useEffect(() => {
    // The stringValue uses '.' as decimal — convert to locale separator for display
    const localized = stringValue.replace('.', localeConfig.decimalSeparator)
    setInternalValue(ensureDecimalFormat(localized))
  }, [stringValue, localeConfig.decimalSeparator, ensureDecimalFormat])

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: ${label ? '8px' : '0'};
        flex: 1 0 0;
        align-self: stretch;
        width: 100%;
      `}
    >
      {label ? (
        <Text
          variant='paragraphSmall'
          color={colors.WHITE}
          weight='regular'
          style={`
            align-self: stretch;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            opacity: ${disabled ? 0.45 : 1};
          `}
        >
          {label}
        </Text>
      ) : null}
      <CurrencyInputField
        min={min}
        id={name}
        name={name}
        prefix={localeConfig.currencySymbol}
        placeholder={`${localeConfig.currencySymbol}0${
          localeConfig.decimalSeparator
        }${'0'.repeat(localeConfig.decimals)}`}
        value={internalValue}
        decimalsLimit={localeConfig.decimals}
        // Pads to the locale's decimals on blur. Never fixedDecimalLength: on blur it
        // reads digits typed without a separator as cents ('50' → 5.00, '123' → 1.23).
        decimalScale={localeConfig.decimals}
        decimalSeparator={localeConfig.decimalSeparator}
        groupSeparator={localeConfig.groupSeparator}
        allowDecimals={true}
        onValueChange={(v, _name, values) => {
          // Update internal value during typing without triggering parent onChange.
          // Preserve an empty field as '' so clearing the input doesn't snap to 0.
          setInternalValue(v ?? '')
          onValueChange?.(values?.float ?? null)
        }}
        onBlur={() => {
          // Normalize locale-specific decimal separator back to '.' for the API value
          const normalized = internalValue.replace(
            localeConfig.decimalSeparator,
            '.'
          )
          onChange(parseFloat(normalized))
        }}
        disabled={disabled}
        allowNegativeValue={false}
        step={1}
        css={[
          css`
            height: 40px !important;
            border-radius: 8px;
            border: 1px solid ${colors.SECONDARY_100};
            background: ${colors.SECONDARY_200};
            width: 100%;
            padding: 6px 8px !important;
            ${paragraphMedium}
            ${weightRegular}
            ${fieldErrors[name] || fieldErrors?.includes(name)
              ? `border-color: ${colors.ERROR};`
              : ''}
            ${disabled ? DISABLED_FIELD_STYLE : `color: ${colors.WHITE};`}
            &:focus {
              outline: none;
              border-color: ${colors.PRIMARY_100};
              box-shadow: 0 0 0 1px ${colors.PRIMARY_100};
            }
            &::placeholder {
              color: ${disabled ? colors.NEUTRAL_200 : colors.NEUTRAL_100};
            }
            transition: border-color 0.15s ease-in-out,
              box-shadow 0.15s ease-in-out;
          `,
          selectStyle,
        ]}
      />
      <ErrorLabel
        fieldErrors={fieldErrors}
        customError={customError}
        name={name}
      />
    </div>
  )
}

export default CurrencyInput
